"use client";

import Image from "next/image";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
  SheetHeader,
} from "@/components/ui/sheet";
import { Menu, User, LayoutDashboard, Settings, ShieldCheck, LogOut, LogIn, UserPlus } from "lucide-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";

interface MobileMenuProps {
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: any;
  logout: () => void;
}

export default function MobileMenu({
  isAuthenticated,
  isAdmin,
  user,
  logout,
}: MobileMenuProps) {
  const pathname = usePathname();

  // Clean navigation links - no duplicates
  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/equipment", label: "Equipment" },
    { href: "/hire", label: "Hire Equipment" },
    { href: "/sale", label: "Buy Equipment" },
    { href: "/about", label: "About Us" },
    { href: "/contact", label: "Contact Us" },
  ];

  return (
    <div className="lg:hidden">
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="hover:bg-[var(--color-rr-green)]/10 h-12 w-12">
            <Menu size={26} />
            <span className="sr-only">Open menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="p-0 w-72 bg-white flex flex-col">
          <VisuallyHidden>
            <SheetHeader>
              <SheetTitle>Navigation Menu</SheetTitle>
            </SheetHeader>
          </VisuallyHidden>
          
          {/* Compact Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-white flex-shrink-0">
            <div onClick={() => window.location.href = "/"} className="flex items-center cursor-pointer">
              <div className="relative w-[120px] h-[35px]">
                <Image
                  src="/logo/logo.svg"
                  alt="RR Equipment Rentals"
                  fill
                  className="object-contain"
                  sizes="120px"
                />
              </div>
            </div>
            <SheetClose asChild>
              <Button 
                variant="outline" 
                size="sm"
                className="rounded-lg hover:bg-gray-100 h-8 px-3 text-xs font-body"
                aria-label="Close menu"
              >
                Close
              </Button>
            </SheetClose>
          </div>


          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-4 py-4">
              {/* Main Navigation */}
              <div className="space-y-1 mb-4">
                <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wider font-body mb-2">
                  Navigation
                </h3>
                {navLinks.map((link) => (
                  <div
                    key={link.href}
                    onClick={() => window.location.href = link.href}
                    className={cn(
                      "flex items-center py-2.5 px-3 rounded-lg transition-all duration-200 cursor-pointer",
                      pathname === link.href
                        ? "bg-[var(--color-rr-green)]/10 text-[var(--color-rr-green)] font-semibold border-l-2 border-[var(--color-rr-green)]"
                        : "text-gray-700 hover:bg-gray-50 hover:text-[var(--color-rr-green)]",
                      "text-sm font-body w-full"
                    )}
                  >
                    {link.label}
                  </div>
                ))}
              </div>

              {/* Admin Section - Only show for authenticated admin users */}
              {isAuthenticated && isAdmin && (
                <div className="space-y-3 pt-4 border-t border-gray-100">
                  <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wider font-body">
                    Admin
                  </h3>
                  
                  {/* Compact Admin Info */}
                  <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 rounded-lg bg-[var(--color-rr-green)]/10 flex items-center justify-center mr-3">
                      <ShieldCheck size={16} className="text-[var(--color-rr-green)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-black font-body truncate">{user?.name || "Admin"}</p>
                      <p className="text-xs text-gray-500 font-body">Admin Panel</p>
                    </div>
                  </div>

                  {/* Admin Actions */}
                  <div className="space-y-1">
                    <Button 
                      onClick={() => window.location.href = "/admin"}
                      variant="ghost" 
                      className="w-full justify-start gap-2 h-9 text-sm font-body hover:bg-gray-50"
                    >
                      <ShieldCheck size={16} /> Admin Panel
                    </Button>
                    <Button
                      onClick={logout}
                      variant="ghost"
                      className="w-full justify-start gap-2 h-9 text-sm font-body text-red-600 hover:bg-red-50"
                    >
                      <LogOut size={16} /> Sign Out
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}